import { ChevronRight, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "./cn";
import { Skeleton } from "./feedback";

export interface Crumb {
  label: string;
  href?: string;
}

export function Breadcrumbs({ items, renderLink }: { items: Crumb[]; renderLink?: (c: Crumb) => ReactNode }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[12.5px] text-muted">
      {items.map((c, i) => (
        <span key={`${c.label}-${i}`} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={12} className="text-subtle" />}
          {c.href && renderLink ? renderLink(c) : <span className={i === items.length - 1 ? "text-fg" : undefined}>{c.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function PageHeader({ title, description, actions, meta, icon: Icon }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; meta?: ReactNode; icon?: LucideIcon }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
      <div className="flex min-w-0 items-center gap-3.5">
        {Icon && (
          <span className="hd-gradient hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-accent sm:flex">
            <Icon size={21} strokeWidth={1.9} />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-[20px] leading-tight font-semibold tracking-tight break-words sm:text-[22px]">{title}</h1>
          {description && <p className="mt-1 text-[13px] text-muted sm:text-[13.5px]">{description}</p>}
          {meta && <div className="mt-2">{meta}</div>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, description, actions, children, className, bodyClassName, flush }: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Remove body padding (tables). */
  flush?: boolean;
}) {
  return (
    <section className={cn("overflow-hidden rounded-2xl border border-line bg-surface shadow-sm", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3.5 sm:px-5">
          <div className="min-w-0">
            {title && <h2 className="text-[14.5px] font-semibold">{title}</h2>}
            {description && <p className="text-[12.5px] text-muted">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn(!flush && "p-4 sm:p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, hint, loading, tone }: { label: string; value: ReactNode; hint?: ReactNode; loading?: boolean; tone?: "default" | "warning" | "danger" | "success" }) {
  const toneClass = tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : tone === "success" ? "text-success" : "text-fg";
  return (
    <div className="min-w-0 px-4 py-3">
      <p className="truncate text-[12px] font-medium tracking-wide text-muted uppercase">{label}</p>
      {loading ? <Skeleton className="mt-1.5 h-6 w-16" /> : <p className={cn("mt-0.5 text-[22px] font-semibold tabular-nums", toneClass)}>{value}</p>}
      {hint && !loading && <p className="truncate text-[12px] text-subtle">{hint}</p>}
    </div>
  );
}

/** Horizontal strip of stats separated by hairlines. */
export function StatGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid divide-line rounded-2xl border border-line bg-surface shadow-sm sm:divide-x", className)}>{children}</div>;
}

export function KeyValue({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[minmax(96px,auto)_1fr] gap-x-4 gap-y-3 sm:grid-cols-[160px_1fr] sm:gap-x-6">
      {items.map((i) => (
        <div key={i.label} className="contents">
          <dt className="text-[12.5px] text-muted sm:text-[13px]">{i.label}</dt>
          <dd className="min-w-0 text-[13.5px] break-words">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Tabs<T extends string>({ value, onChange, items, className }: { value: T; onChange(v: T): void; items: { value: T; label: string; icon?: LucideIcon }[]; className?: string }) {
  const { ref, style } = useScrollFades<HTMLDivElement>();
  return (
    <div ref={ref} style={style} role="tablist" className={cn("mb-5 flex max-w-full gap-1 overflow-x-auto rounded-xl border border-line bg-surface p-1 shadow-xs [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", className)}>
      {items.map((i) => {
        const active = value === i.value;
        const Icon = i.icon;
        return (
          <button
            key={i.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(i.value)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors",
              active ? "bg-accent-soft text-accent shadow-xs" : "text-muted hover:bg-surface-2 hover:text-fg",
            )}
          >
            {Icon && <Icon size={15} strokeWidth={active ? 2.1 : 1.8} />}
            {i.label}
          </button>
        );
      })}
    </div>
  );
}

export interface SideTabGroup<T extends string> {
  label: string;
  items: { value: T; label: string; icon?: LucideIcon }[];
}

/** Horizontal scroller with edge fades that appear only when there is more to scroll. */
function useScrollFades<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [fade, setFade] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setFade({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);
  const mask =
    fade.left || fade.right
      ? `linear-gradient(90deg, ${fade.left ? "transparent, #000 28px" : "#000"}, ${fade.right ? "#000 calc(100% - 28px), transparent" : "#000"})`
      : undefined;
  return { ref, style: mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined };
}

/**
 * Grouped section navigation: a vertical, sticky list on wide screens (xl+) and
 * a horizontally scrolling pill bar below that. One tablist either way, so
 * every tab keeps a single accessible element. The active tab is scrolled into view.
 */
export function SideTabs<T extends string>({ value, onChange, groups, className }: { value: T; onChange(v: T): void; groups: SideTabGroup<T>[]; className?: string }) {
  const { ref, style } = useScrollFades<HTMLDivElement>();
  useEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    const box = ref.current;
    if (!el || !box || box.scrollWidth <= box.clientWidth) return;
    box.scrollTo({ left: el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [value, ref]);
  return (
    <div className={cn("min-w-0 rounded-2xl border border-line bg-surface shadow-sm xl:sticky xl:top-24", className)}>
      <div ref={ref} role="tablist" style={style} className="flex gap-1 overflow-x-auto p-1.5 [scrollbar-width:none] xl:flex-col xl:overflow-visible xl:p-2.5 [&::-webkit-scrollbar]:hidden">
        {groups.map((g, gi) => (
          <div key={g.label} className={cn("contents xl:block", gi > 0 && "xl:mt-3")}>
            <p className="hidden px-2.5 pb-1 text-[10.5px] font-semibold tracking-[0.08em] text-subtle uppercase xl:block" aria-hidden>
              {g.label}
            </p>
            {g.items.map((i) => {
              const active = value === i.value;
              const Icon = i.icon;
              return (
                <button
                  key={i.value}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => onChange(i.value)}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium whitespace-nowrap transition-colors xl:flex xl:w-full xl:gap-2.5",
                    active ? "bg-accent-soft text-accent" : "text-muted hover:bg-surface-2 hover:text-fg",
                  )}
                >
                  {Icon && <Icon size={16} strokeWidth={active ? 2.2 : 1.8} />}
                  {i.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
